import type { AlertColor } from "@mui/material";
import { Alert, Slide, Snackbar } from "@mui/material";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerSnackbar } from "../HTTPClient";

export type SnackbarContextProps = {
  snackbarShowMessage: (message: string, severity?: AlertColor, duration?: number) => void;
  defaultError: () => void;
};

type SnackbarMessage = {
  id: number;
  message: string;
  severity: AlertColor;
  duration: number;
};

const SnackbarContext = createContext<SnackbarContextProps | null>(null);

const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);
  const { t } = useTranslation();

  const showMessage = (message: string, severity: AlertColor = "success", duration = 10000) => {
    const id = Date.now().toString();
    setMessages((prevMessages) => [...prevMessages, { id, message, severity, duration }]);
  };

  const handleClose = (id: number) => {
    setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
  };

  // Enregistrer la fonction de snackbar pour qu'elle soit accessible globalement
  useEffect(() => {
    registerSnackbar(showMessage);
    // Pas besoin de cleanup car registerSnackbar est idempotent
  }, []);

  const contextValue: SnackbarContextProps = {
    snackbarShowMessage: showMessage,
    defaultError: () => showMessage(t("defaultError"), "error"),
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {messages.map(({ id, message, severity, duration }) => (
        <Snackbar
          key={id}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          autoHideDuration={duration}
          open={true}
          onClose={() => handleClose(id)}
          TransitionComponent={Slide}
        >
          <Alert variant="filled" onClose={() => handleClose(id)} severity={severity}>
            {message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
};

const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export { SnackbarProvider, useSnackbar };
